import React from "react";
import { Clock, AlertTriangle, AlertOctagon, Pause, Check } from "lucide-react";
import { fmtDateTime } from "../lib/auditFormat";

/**
 * Compact business-hour SLA badge.
 *
 * Reads ``task.sla`` produced by the backend (see /api/tasks?with_sla=true).
 * Renders a coloured chip with remaining (or overdue) working minutes and an
 * icon hinting status. Hidden if the task has no SLA budget (e.g. PDI_READY).
 */
const STATUS_META = {
  on_track:           { label: "On Track",      cls: "bg-emerald-50 text-emerald-700 ring-emerald-200", Icon: Clock },
  at_risk:            { label: "At Risk",       cls: "bg-amber-50 text-amber-700 ring-amber-200",       Icon: AlertTriangle },
  delayed:            { label: "Delayed",       cls: "bg-rose-50 text-rose-700 ring-rose-200",          Icon: AlertOctagon },
  on_hold:            { label: "Transit Hold",  cls: "bg-sky-50 text-sky-700 ring-sky-200",             Icon: Pause },
  paused:             { label: "SLA Paused",    cls: "bg-sky-50 text-sky-700 ring-sky-200",             Icon: Pause },
  completed_on_time:  { label: "On Time",       cls: "bg-emerald-50 text-emerald-700 ring-emerald-200", Icon: Check },
  completed_late:     { label: "Closed Late",   cls: "bg-rose-50 text-rose-700 ring-rose-200",          Icon: AlertOctagon },
};

function fmtMinutes(m) {
  const abs = Math.abs(Math.round(m));
  if (abs < 60) return `${abs}m`;
  const h = Math.floor(abs / 60);
  const r = abs % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
}

export default function SlaBadge({ task, dense = false }) {
  const sla = task?.sla;
  if (!sla || !sla.budget_minutes) return null;
  const meta = STATUS_META[sla.status] || STATUS_META.on_track;
  const { Icon, cls, label } = meta;
  const isOverdue = sla.status === "delayed" || sla.status === "completed_late";
  const detail = (() => {
    if (sla.status === "paused") return sla.pause_remarks ? "Paused" : "Paused";
    if (sla.status === "on_hold") return "Paused";
    if (sla.status === "completed_on_time" || sla.status === "completed_late") {
      return `${fmtMinutes(sla.elapsed_minutes)}/${fmtMinutes(sla.budget_minutes)}`;
    }
    if (isOverdue) return `+${fmtMinutes(Math.abs(sla.remaining_minutes))} over`;
    return `${fmtMinutes(sla.remaining_minutes)} left`;
  })();
  return (
    <span
      data-testid="sla-badge"
      data-sla-status={sla.status}
      title={sla.due_at ? `Due: ${fmtDateTime(sla.due_at)}` : undefined}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${cls} ${dense ? "" : "leading-tight"}`}
    >
      <Icon className="h-3 w-3" />
      <span>{label}</span>
      <span className="opacity-70">·</span>
      <span className="tabular-nums">{detail}</span>
    </span>
  );
}
